import type { UrlObject } from "node:url";

import type { LinkProps as NextLinkProps } from "next/link";

import type {
  NavRoutesConfig,
  NavRoutesConfigItemSub,
} from "#app/_ui/components/headers/nav-routes-config";

import type { IconName } from "./icon.names";

// -----------------------------------------------------------------------------

export type NavRouteSub = {
  id: string;
  iconName?: IconName;
  element: React.ReactElement<any> | string;
  path: NextLinkProps["href"];
};

export type NavRoute = {
  id: string;
  iconName: IconName;
  element: React.ReactElement<any> | string;
} & (
  | {
      path: NextLinkProps["href"];
      paths?: never;
    }
  | {
      path?: never;
      paths: NavRouteSub[];
    }
);

export type NavRoutes = NavRoute[];

// -----------------------------------------------------------------------------

export const routeIconNameMap = {
  Home: "ms-home-filled",
  Investments: "ms-pie-chart-filled",
  ThreeSixtyWealthAndPlanning: "ms-track-changes-filled",
  InvestingTools: "ms-bar-chart-4-bars-filled",
  AccountServices: "ms-how-to-reg-filled",
  Documents: "ms-description-filled",
  BairdTrust: "custom-timeline",
  ProfileAndSettings: "ms-person-filled",
} as const satisfies Record<string, IconName>;

export function toRouteIconName(key: string) {
  const iconName: IconName =
    routeIconNameMap[key as keyof typeof routeIconNameMap] ??
    "custom-not-applicable";
  return iconName;
}

export function parseRoutesConfig(routesConfig: NavRoutesConfig): NavRoutes {
  return Object.entries(routesConfig).map(([id, route]) => {
    const iconName = route.iconName ?? "custom-not-applicable";
    const element = route.element;
    if ("paths" in route) {
      const paths = parseRoutesConfigSub(route.paths!);
      return { id, iconName, element, paths };
    } else {
      const path = route.path;
      return { id, iconName, element, path };
    }
  });
}

export function parseRoutesConfigSub(
  routesConfigSub: Record<string, NavRoutesConfigItemSub>,
) {
  return Object.entries(routesConfigSub).map(([id, route]) => {
    const element = route.element;
    const path = route.path;
    return { id, element, path };
  });
}

export function extractPathname(value: UrlObject | string) {
  if (typeof value === "string" && value.includes("?")) {
    return value.split("?").at(0)!;
  }
  if (typeof value === "string") {
    return value;
  }
  if (
    typeof value === "object" &&
    "pathname" in value &&
    typeof value.pathname === "string"
  ) {
    return value.pathname;
  }
  return "";
}

export function pathIsActive({
  pathname,
  value,
}: {
  pathname: string;
  value: UrlObject | string;
}) {
  const val = extractPathname(value);
  if (val === pathname) {
    return true;
  }
  // Warning: deep routes could mess with active nav states
  // "/a/b" and "/a/b/c" match
  if (
    pathname.startsWith(val + "/") &&
    pathname.split("/").filter(Boolean).length >= 3
  ) {
    return true;
  }
  return false;
}

export function findActiveRouteId(
  routes: NavRoutes,
  activePath: string,
): string | undefined {
  return routes.find(
    (route) =>
      (route.path &&
        pathIsActive({ pathname: activePath, value: route.path })) ||
      (route.paths &&
        route.paths.find((routeChild) =>
          pathIsActive({ pathname: activePath, value: routeChild.path }),
        )),
  )?.id;
}

// -----------------------------------------------------------------------------

export const mockRoutes = [
  {
    id: "link-0",
    iconName: "ms-help",
    element: "Link 0",
    path: "/link-0",
  },
  {
    id: "trigger-0",
    iconName: "ms-help",
    element: "Trigger 0",
    paths: [
      {
        id: "trigger-0-link-0",
        path: "/trigger-0/link-0",
        element: "Link 0.0",
      },
      {
        id: "trigger-0-link-1",
        path: "/trigger-0/link-1",
        element: "Link 0.1",
      },
      {
        id: "trigger-0-link-2",
        path: "/trigger-0/link-2",
        element: "Link 0.2",
      },
    ],
  },
  {
    id: "link-1",
    iconName: "ms-help",
    element: "Link 1",
    path: "/link-1",
  },
  {
    id: "trigger-1",
    iconName: "ms-help",
    element: "Trigger 1",
    paths: [
      {
        id: "trigger-1-link-0",
        element: "Link 1.0",
        path: "/trigger-1/link-0",
      },
      {
        id: "trigger-1-link-1",
        element: "Link 1.1",
        path: "/trigger-1/link-1",
      },
      {
        id: "trigger-1-link-2",
        element: "Link 1.2",
        path: "/trigger-1/link-2",
      },
    ],
  },
  {
    id: "link-2",
    iconName: "ms-help",
    element: "Link 2",
    path: "/link-2",
  },
  {
    id: "trigger-2",
    iconName: "ms-help",
    element: "Trigger 2",
    paths: [
      {
        id: "trigger-2-link-0",
        element: "Link 2.0",
        path: "/trigger-2/link-0",
      },
      {
        id: "trigger-2-link-1",
        element: "Link 2.1",
        path: "/trigger-2/link-1",
      },
      {
        id: "trigger-2-link-2",
        element: "Link 2.2",
        path: "/trigger-2/link-2",
      },
    ],
  },
] as const satisfies NavRoutes;

export const mockRoutesApp = [
  {
    id: "Home",
    iconName: "ms-home-filled",
    element: "Home",
    path: "/",
  },

  {
    id: "Investments",
    iconName: "ms-pie-chart-filled",
    element: "Investments",
    paths: [
      {
        id: "InvestmentsAccounts",
        element: "Accounts",
        path: "/investments/accounts",
      },
      {
        id: "InvestmentsHoldings",
        element: "Holdings",
        path: "/investments/holdings",
      },
      {
        id: "InvestmentsActivity",
        element: "Activity",
        path: "/investments/activity",
      },
      {
        id: "InvestmentsPerformance",
        element: "Performance",
        path: "/investments/performance",
      },
      {
        id: "InvestmentsOrders",
        element: "Orders",
        path: "/investments/orders",
      },
    ],
  },

  {
    id: "ThreeSixtyWealthAndPlanning",
    iconName: "ms-track-changes-filled",
    element: "360 Wealth & Planning",
    paths: [
      {
        id: "ThreeSixtyWealthOverview",
        element: "360 Wealth Overview",
        path: "/360-wealth",
      },
      {
        id: "PlanningOverview",
        element: "Planning",
        path: "/planning",
      },
      {
        id: "PlanningMyPlan",
        element: "My Plan",
        path: "/planning/my-plan",
      },
      {
        id: "ThreeSixtyWealthAccounts",
        element: "Accounts",
        path: "/360-wealth/accounts",
      },
      {
        id: "ThreeSixtyWealthTransactions",
        element: "Transactions",
        path: "/360-wealth/transactions",
      },
      {
        id: "ThreeSixtyWealthSpending",
        element: "Spending",
        path: "/360-wealth/spending",
      },
      {
        id: "ThreeSixtyWealthInvestments",
        element: "Investments",
        path: "/360-wealth/investments",
      },
      {
        id: "ThreeSixtyWealthSettings",
        element: "360 Wealth Settings",
        path: "/360-wealth/settings",
      },
      {
        id: "ThreeSixtyWealthFaqs",
        element: "360 Wealth FAQs",
        path: "/360-wealth/faqs",
      },
    ],
  },

  {
    id: "InvestingTools",
    iconName: "ms-bar-chart-4-bars-filled",
    element: "Investing Tools",
    paths: [
      {
        id: "InvestingToolsUsMarkets",
        element: "US Markets",
        path: "/investing-tools/us-markets",
      },
      {
        id: "InvestingToolsBairdResearch",
        element: "Baird Research",
        path: "/investing-tools/baird-research",
      },
      {
        id: "InvestingToolsMarketBriefing",
        element: "Market Briefing",
        path: "/investing-tools/market-briefing",
      },
      {
        id: "InvestingToolsPortfolioTracker",
        element: "Portfolio Tracker",
        path: "/investing-tools/portfolio-tracker",
      },
      {
        id: "InvestingToolsWatchlist",
        element: "Watchlist",
        path: "/investing-tools/watchlist",
      },
      {
        id: "InvestingToolsStocks",
        element: "Stocks",
        path: "/investing-tools/stocks",
      },
      {
        id: "InvestingToolsNews",
        element: "News",
        path: "/investing-tools/news",
      },
      {
        id: "InvestingToolsAlerts",
        element: "Alerts",
        path: "/investing-tools/alerts",
      },
    ],
  },

  {
    id: "AccountServices",
    iconName: "ms-how-to-reg-filled",
    element: "Account Services",
    paths: [
      {
        id: "AccountServicesOpenAccount",
        element: "Open Account",
        path: "/account-services/open-account",
      },
      {
        id: "AccountServicesEbill",
        element: "eBill",
        path: "/account-services/ebill",
      },
      {
        id: "AccountServicesTransfers",
        element: "Transfers",
        path: "/account-services/transfers/transfer-form",
      },
      {
        id: "AccountServicesAwards",
        element: "Awards",
        path: "/account-services/awards",
      },
    ],
  },

  {
    id: "Documents",
    iconName: "ms-description-filled",
    element: "Documents",
    paths: [
      {
        id: "DocumentsOverview",
        element: "All Documents",
        path: "/documents",
      },
      {
        id: "DocumentsReceived",
        element: "Document Exchange",
        path: "/documents/received",
      },
      {
        id: "DocumentsStatements",
        element: "Statements",
        path: "/documents/statements",
      },
      {
        id: "DocumentsTaxForms",
        element: "Tax Forms",
        path: "/documents/tax-forms",
      },
      {
        id: "DocumentsConfirmations",
        element: "Confirmations",
        path: "/documents/confirmations",
      },
      {
        id: "DocumentsClientCommunications",
        element: "Client Communications",
        path: "/documents/client-communications",
      },
      {
        id: "DocumentsChecks",
        element: "Checks",
        path: "/documents/checks",
      },
      {
        id: "DocumentsFinancialPlans",
        element: "Financial Plans",
        path: "/documents/financial-plans",
      },
      {
        id: "DocumentsPerformance",
        element: "Performance",
        path: "/documents/performance",
      },
    ],
  },

  {
    id: "BairdTrust",
    iconName: "custom-timeline",
    element: "Baird Trust",
    paths: [
      {
        id: "BairdTrustOverview",
        element: "Dashboard",
        path: "/baird-trust",
      },
      {
        id: "BairdTrustGainsAndLosses",
        element: "Gains & Losses",
        path: "/baird-trust/gains-and-losses",
      },
      {
        id: "BairdTrustPortfolioDiversification",
        element: "Portfolio Diversification",
        path: "/baird-trust/portfolio-diversification",
      },
      {
        id: "BairdTrustAccounts",
        element: "Accounts",
        path: "/baird-trust/accounts",
      },
      {
        id: "BairdTrustHoldings",
        element: "Holdings",
        path: "/baird-trust/holdings",
      },
      {
        id: "BairdTrustDocuments",
        element: "Documents",
        path: "/baird-trust/documents",
      },
    ],
  },

  {
    id: "ProfileAndSettings",
    iconName: "ms-person-filled",
    element: "Profile & Settings",
    paths: [
      {
        id: "ProfileAndSettingsPersonalInformation",
        element: "Personal Information",
        path: "/profile-and-settings/personal-information",
      },
      {
        id: "ProfileAndSettingsMailingAddresses",
        element: "Mailing Addresses",
        path: "/profile-and-settings/mailing-addresses",
      },
      {
        id: "ProfileAndSettingsUserIdAndPassword",
        element: "User ID & Password",
        path: "/profile-and-settings/user-id-and-password",
      },
      {
        id: "ProfileAndSettingsPaperlessPreferences",
        element: "Paperless Preferences",
        path: "/profile-and-settings/paperless-preferences",
      },
      {
        id: "ProfileAndSettingsAccountNicknames",
        element: "Account Nicknames",
        path: "/profile-and-settings/account-nicknames",
      },
      {
        id: "ProfileAndSettingsAccountGroups",
        element: "Account Groups",
        path: "/profile-and-settings/account-groups",
      },
      {
        id: "ProfileAndSettingsAccountAccess",
        element: "Account Access",
        path: "/profile-and-settings/account-access",
      },
    ],
  },
] as const satisfies NavRoutes;
